import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';

import Heading from '@jpp/atoms/Heading';
import useCartTotals from '@jpp/hooks/useCartTotals';
import useToggle from '@jpp/hooks/useToggle';
import { Button } from '@jpp/molecules/Button/Button';
import PaymentRequestButton from '@jpp/molecules/PaymentRequestButton';
import { SELL_BLOCK_ROOT_CLASSNAME } from '@jpp/molecules/SellBlock/utils';
import { AuthModal } from '@jpp/organisms/Modal';
import { EAuthModalTab } from '@jpp/organisms/Modal/AuthModal/AuthModal';
import { getAuthIsLoggedIn } from 'common/redux/auth/selectors';
import { IReduxState } from 'common/redux/createStore';
import {
  ICartCoupon,
  ICartLineItem,
} from 'common/redux/wooCommerce/cart/reducer';
import {
  getCartCoupons,
  getCartLineItems,
} from 'common/redux/wooCommerce/cart/selectors';
import { ECtaTheme, EPageType } from 'common/typings/enums';
import { hasSubscriptionProducts } from 'common/utils/shared/cart';
import {
  getPriceWithCurrency,
  getPriceWithoutCurrency,
  getSubTotal,
} from 'common/utils/shared/product';

import ConfigProvider from '../../../../services/configProvider';
import './CartSubTotal.scss';

interface ICartSubTotalProps {
  className?: string;
  totalItems: number;
  isCheckoutDisabled?: boolean;
  hasBackorderItems?: boolean;
}

const ROOT_CLASSNAME = 'CartSubTotal';

const CartSubTotal: React.FunctionComponent<ICartSubTotalProps> = ({
  className,
  totalItems,
  isCheckoutDisabled = false,
  hasBackorderItems = false,
}) => {
  const {
    cartTotals: { subtotal_tax, discount_tax, discount_total, ...cartTotals },
    discount,
    totalAmount,
    shippingAmount,
  } = useCartTotals();
  const [isModalVisible, toggleIsModalVisible, setIsModalVisible] =
    useToggle(false);
  const { coupons, isLoggedIn, cartLineItems } = useSelector<
    IReduxState,
    {
      coupons: ICartCoupon[];
      isLoggedIn: boolean;
      cartLineItems: ICartLineItem[];
    }
  >((state) => ({
    coupons: getCartCoupons(state),
    isLoggedIn: getAuthIsLoggedIn(state),
    cartLineItems: getCartLineItems(state),
  }));
  const hasSubscriptionProds = hasSubscriptionProducts(cartLineItems);
  const isDisabled =
    isCheckoutDisabled || (!isLoggedIn && hasSubscriptionProds);
  const subtotal = getPriceWithoutCurrency(
    getSubTotal(cartTotals.subtotal, subtotal_tax, discount_total, discount_tax)
  );
  const creditDiscount = parseFloat(subtotal) + shippingAmount - totalAmount;
  const hasNoDiscount = discount === 0;

  return (
    <>
      <article className={classNames(ROOT_CLASSNAME, className)}>
        <Heading
          priority={3}
          className={`${ROOT_CLASSNAME}__heading`}
          isInnerHTML={false}
        >
          <span className={`${ROOT_CLASSNAME}__copy`}>Total Items</span>
          <span className={`${ROOT_CLASSNAME}__price`}>{totalItems}</span>
        </Heading>

        {discount > 0 && (
          <Heading
            priority={3}
            className={`${ROOT_CLASSNAME}__heading`}
            isInnerHTML={false}
          >
            <span className={`${ROOT_CLASSNAME}__copy`}>Discounts applied</span>
            <span className={`${ROOT_CLASSNAME}__price`}>
              -{getPriceWithCurrency(discount)}
            </span>
          </Heading>
        )}

        <Heading
          priority={3}
          className={`${ROOT_CLASSNAME}__heading`}
          isInnerHTML={false}
        >
          <span className={`${ROOT_CLASSNAME}__copy`}>Cart Subtotal</span>
          <span className={`${ROOT_CLASSNAME}__price`}>{subtotal}</span>
        </Heading>

        {hasNoDiscount && creditDiscount !== 0 && coupons.length > 0 && (
          <Heading
            priority={3}
            className={`${ROOT_CLASSNAME}__heading`}
            isInnerHTML={false}
          >
            <span className={`${ROOT_CLASSNAME}__copy`}>Gift Card applied</span>
            <span className={`${ROOT_CLASSNAME}__price`}>
              -{getPriceWithCurrency(creditDiscount)}
            </span>
          </Heading>
        )}

        <p className={`${ROOT_CLASSNAME}__copy ${ROOT_CLASSNAME}__copy--long`}>
          Delivery and taxes calculated during checkout
        </p>

        {isDisabled && (
          <>
            <p className={`${ROOT_CLASSNAME}__checkout-oos`}>
              {hasSubscriptionProds && !isLoggedIn && (
                <span className={`${ROOT_CLASSNAME}__subscription-disclaimer`}>
                  You must have an account to set up a subscription.{' '}
                  <span
                    className={`${ROOT_CLASSNAME}__subscription-disclaimer-button`}
                    role="button"
                    onClick={() => setIsModalVisible(true)}
                  >
                    Please sign up or login here
                  </span>{' '}
                  to proceed. Alternatively remove the item from your bag.
                </span>
              )}
            </p>
            <p className={`${ROOT_CLASSNAME}__checkout-oos`}>
              Some of your items are sold out
            </p>
          </>
        )}

        <div
          className={classNames(`${ROOT_CLASSNAME}__cta-wrapper`, {
            [`${ROOT_CLASSNAME}__cta-wrapper--checkout-disabled`]: isDisabled,
          })}
        >
          {ConfigProvider.getValue('APPLE_PAY_ENABLED') !== 'false' &&
            !isDisabled &&
            coupons.length === 0 &&
            !hasSubscriptionProds && (
              <PaymentRequestButton
                className={`${ROOT_CLASSNAME}__payment-request`}
              />
            )}

          <br />

          <Button
            behaviour="router"
            className={classNames(`${ROOT_CLASSNAME}__checkout`, {
              [`${ROOT_CLASSNAME}__checkout--is-disabled`]: isDisabled,
            })}
            theme={isDisabled ? ECtaTheme.TintPsi : ECtaTheme.Brand}
            link={`/${EPageType.Checkout}`}
            disabled={isDisabled}
          >
            Checkout Now
          </Button>

          <Button
            behaviour="router"
            theme={ECtaTheme.BrandOutline}
            link={`/${EPageType.Shop}`}
          >
            Continue Shopping
          </Button>
        </div>
      </article>

      {isModalVisible && !isLoggedIn && (
        <AuthModal
          isVisible={isModalVisible}
          className={`${SELL_BLOCK_ROOT_CLASSNAME}__auth-modal`}
          onClose={toggleIsModalVisible}
          bodyOverflow={false}
          defaultTab={EAuthModalTab.Register}
        />
      )}
    </>
  );
};

export default CartSubTotal;
